<script setup lang="ts">
/**
 * BookingsFallback.vue — Mock-First Fallback Contact Form
 * Task: 113-111-T003
 *
 * This is a mock-first placeholder for the real contact form from Feature 113-120.
 * When 113-120 is delivered, replace the mock form fields inside the UCard with the
 * real 113-120 contact form component. The role="alert" wrapper, errorMessage prop,
 * and emit('submit') interface MUST NOT change during that swap.
 *
 * 113-120 SWAP-IN POINT: Replace mock <form> contents below with <ContactForm /> component
 */

const props = defineProps<{
  errorMessage?: string
}>()

const emit = defineEmits<{
  submit: []
}>()

const DEFAULT_MESSAGE =
  'Our booking system is currently unavailable. Please use the form below to send us a message and we will be in touch shortly.'

const displayMessage = computed(() => props.errorMessage ?? DEFAULT_MESSAGE)

const formData = reactive({
  name: '',
  email: '',
  message: '',
})

function handleSubmit(): void {
  emit('submit')
}
</script>

<template>
  <div
    role="alert"
    aria-live="polite"
    class="bookings-fallback"
  >
    <UCard>
      <template #header>
        <h2 class="text-lg font-semibold">
          Get in Touch
        </h2>
        <p class="mt-1 text-sm text-gray-600">
          {{ displayMessage }}
        </p>
      </template>

      <!-- 113-120 SWAP-IN POINT: Replace this mock form with <ContactForm /> -->
      <form
        novalidate
        @submit.prevent="handleSubmit"
      >
        <div class="space-y-4">
          <div>
            <label
              for="fallback-name"
              class="block text-sm font-medium"
            >
              Name
            </label>
            <UInput
              id="fallback-name"
              v-model="formData.name"
              type="text"
              name="name"
              autocomplete="name"
              class="mt-1"
              placeholder="Your name"
            />
          </div>

          <div>
            <label
              for="fallback-email"
              class="block text-sm font-medium"
            >
              Email
            </label>
            <UInput
              id="fallback-email"
              v-model="formData.email"
              type="email"
              name="email"
              autocomplete="email"
              class="mt-1"
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label
              for="fallback-message"
              class="block text-sm font-medium"
            >
              Message
            </label>
            <UTextarea
              id="fallback-message"
              v-model="formData.message"
              name="message"
              :rows="4"
              class="mt-1"
              placeholder="How can we help?"
            />
          </div>
        </div>

        <div class="mt-6">
          <UButton
            type="submit"
            block
            label="Send Message"
          />
          <p class="mt-3 text-xs text-gray-600">
            By submitting this form, you consent to us using your details to respond.
            See our
            <NuxtLink
              to="/privacy"
              class="underline"
            >
              Privacy Policy
            </NuxtLink>.
          </p>
        </div>
      </form>
    </UCard>
  </div>
</template>
